import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Users, BarChart3, BellRing, Activity, Shield, Database, Settings, History,
  ArrowUpRight, Search, Sparkles, Receipt, LifeBuoy,
} from 'lucide-react';
import AppShell from './AppShell';
import { useT } from '../i18n/I18nProvider';
import { useSession } from '../session/SessionProvider';
import { Dropdown, DropdownLabel, Input, Badge, Button } from '../ui';
import api from '../services/api';

export default function OwnerLayout() {
  const t = useT();
  const nav = [
    {
      group: t('nav.groups.platform'),
      items: [
        { to: '/owner', end: true, label: t('nav.overview'), icon: LayoutDashboard },
        { to: '/owner/companies', label: t('nav.companies'), icon: Building2 },
        { to: '/owner/billing', label: t('nav.billingNav'), icon: Receipt },
        { to: '/owner/employees', label: t('nav.employees'), icon: Users },
      ],
    },
    {
      group: t('nav.groups.business'),
      items: [
        { to: '/owner/analytics', label: t('nav.analytics'), icon: BarChart3 },
        { to: '/owner/notifications', label: t('nav.notifications'), icon: BellRing },
        { to: '/owner/support', label: t('nav.support'), icon: LifeBuoy },
      ],
    },
    {
      group: t('nav.groups.system'),
      items: [
        { to: '/owner/audit', label: t('nav.audit'), icon: History },
        { to: '/owner/logs', label: t('nav.logs'), icon: Activity },
        { to: '/owner/security', label: t('nav.security'), icon: Shield },
        { to: '/owner/sql', label: t('nav.sql'), icon: Database },
        { to: '/owner/settings', label: t('nav.systemSettings'), icon: Settings },
      ],
    },
  ];

  const brand = (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white shadow-sm">
        <Sparkles className="h-4 w-4" />
      </span>
      <div className="leading-tight">
        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{t('app.name')}</p>
        <p className="text-[11px] text-zinc-500 dark:text-zinc-400">{t('app.ownerConsole')}</p>
      </div>
    </div>
  );

  return <AppShell nav={nav} brand={brand} topbarExtra={<CompanyPicker />} />;
}

/** Быстрый вход в кабинет любой компании */
function CompanyPicker() {
  const t = useT();
  const navigate = useNavigate();
  const { enterCompany } = useSession();
  const [companies, setCompanies] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    api.get('/admin/tenants').then((res) => setCompanies(res.data.data || [])).catch(() => {});
  }, []);

  const filtered = companies.filter((c) =>
    `${c.name} ${c.company_code ?? ''}`.toLowerCase().includes(query.trim().toLowerCase())
  );

  const open = (company) => {
    enterCompany(company);
    navigate('/company');
  };

  return (
    <Dropdown
      align="left"
      className="w-80"
      trigger={(isOpen, toggle) => (
        <Button variant="secondary" size="sm" icon={ArrowUpRight} onClick={toggle} aria-expanded={isOpen}>
          <span className="hidden sm:inline">{t('topbar.openCompany')}</span>
        </Button>
      )}
    >
      <div className="p-1.5">
        <Input icon={Search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('common.search')} autoFocus />
      </div>
      <DropdownLabel>{t('topbar.chooseCompany')}</DropdownLabel>
      <div className="max-h-72 overflow-y-auto custom-scrollbar">
        {filtered.map((c) => (
          <button
            key={c.id}
            type="button"
            data-close
            onClick={() => open(c)}
            className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <span className="truncate text-zinc-800 dark:text-zinc-100">{c.name}</span>
            <span className="flex shrink-0 items-center gap-1.5">
              {c.company_code && <span className="font-mono text-[11px] text-zinc-400">{c.company_code}</span>}
              {c.status !== 'active' && <Badge tone="warning">{c.status}</Badge>}
            </span>
          </button>
        ))}
        {!filtered.length && <p className="px-2.5 py-3 text-sm text-zinc-500">{t('common.noData')}</p>}
      </div>
    </Dropdown>
  );
}
